import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Event, NavigationEnd, NavigationStart, Router, RouterEvent } from '@angular/router';
import { SettingType } from '@app/api-module/control-centre-api-client';
import { RoleEnum } from '@app/api-module/user-management-api-client-v2';
import { Org } from '@app/models';
import { Tab } from '@app/models/tab';
import { ReferralsService } from '@app/referrals/referrals.service';
import { FeaturesService } from '@app/services/features.service';
import { PermissionRoutingService } from '@app/shared/permission-routing.service';
import { SettingUtilities } from '@app/shared/setting-utilities';
import { BehaviorSubject, combineLatest, filter, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { AuthenticationService } from '../authentication/services/authentication.service';
import { OrgSiteSelectionService } from '../shared/org-site-selection/org-site-selection.service';
import { WorklistService } from '../worklist/worklist.service';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DashboardComponent implements OnDestroy {
    private static readonly hasSetInitialTab: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private static readonly DASHBOARD_PATH: string = '/dashboard';
    private static readonly INTERVAL_UNACKNOWLEDGED_REFERRALS_PERIODIC_REFRESH = 60000; // 1 minute

    dashboardTabs: DashboardTabs = {
        tabItems: {
            [Tab.Orders]: {
                htmlId: 'tab-orders',
                title: 'Orders',
                labelCount: '',
                routerLink: 'orders',
                isVisible: false
            },
            [Tab.Referrals]: {
                htmlId: 'tab-referrals',
                title: 'Referrals',
                labelCount: '',
                routerLink: 'referrals',
                isVisible: false
            },
            [Tab.ClinicalFeedback]: {
                htmlId: 'tab-clinical-feedback',
                title: 'Clinical Feedback',
                routerLink: 'clinical-feedback',
                isVisible: false
            },
            [Tab.ImagingRequests]: {
                htmlId: 'tab-imaging-requests',
                title: 'Imaging Requests',
                routerLink: 'imaging-requests',
                isVisible: false
            },
            [Tab.DemandManagement]: {
                htmlId: 'tab-demand-management',
                title: 'Demand Management',
                routerLink: 'demand-management',
                isVisible: false
            }
        },
        *[Symbol.iterator]() {
            for (const [_, value] of Object.entries(this.tabItems as Record<Tab, TabOptions>)) {
                yield value;
            }
        }
    };

    private readonly destroy$ = new Subject();
    private readonly referralsTabVisible$ = new BehaviorSubject(false);

    hasReferralsAwaitingAcknowledgement = false;

    private readonly _subscriptions = new Subscription();

    constructor(
        private readonly router: Router,
        private readonly permissionRoutingService: PermissionRoutingService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        readonly worklistService: WorklistService,
        private readonly referralsService: ReferralsService,
        private readonly authenticationService: AuthenticationService,
        private readonly featuresService: FeaturesService
    ) {
        const orgSelected$ = this.orgSiteSelectionService.getOrgSelected$();
        const hasOnlyOneSite$ = this.orgSiteSelectionService.hasOnlyOneSite();
        const sitesSelected$ = this.orgSiteSelectionService.getSitesSelected();

        const userSite$ = combineLatest([orgSelected$, sitesSelected$, hasOnlyOneSite$]);

        this._subscriptions.add(
            orgSelected$.subscribe(selectedOrg => {
                if (selectedOrg.orgCode) {
                    this.onSelectedOrgChanged(selectedOrg);
                }
            })
        );

        this._subscriptions.add(
            router.events
                .pipe(filter((event: Event | RouterEvent) => event instanceof NavigationStart || event instanceof NavigationEnd))
                .subscribe((event: RouterEvent) => {
                    this.onTabNavigation(event);
                })
        );

        this._subscriptions.add(
            worklistService.ordersCount$.pipe(debounceTime(0)).subscribe(count => {
                this.onOrdersCountUpdated(count);
            })
        );

        this._subscriptions.add(
            referralsService.referralsCount$.pipe(debounceTime(0)).subscribe(count => {
                this.onReferralsCountUpdated(count);
            })
        );

        this._subscriptions.add(
            combineLatest([
                this.referralsTabVisible$,
                userSite$,
                this.referralsService.checkReferralsAwaitingAcknowledgement$,
                timer(0, DashboardComponent.INTERVAL_UNACKNOWLEDGED_REFERRALS_PERIODIC_REFRESH).pipe(takeUntil(this.destroy$))
            ])
                .pipe(
                    filter(
                        ([referralsTabVisible, [_org, _siteIds, _hasOnlyOneSite], _checkReferralsAwaitingAcknowledgement, _timer]) =>
                            referralsTabVisible
                    ),
                    switchMap(([_referralsTabVisible, [org, siteIds, hasOnlyOneSite], _checkReferralsAwaitingAcknowledgement, _timer]) => {
                        const { orgWide } = org.getAllowedSites();

                        // if user has org wide permission, and user is not selecting a single site, show referrals from the org
                        if (orgWide && (siteIds.length !== 1 || hasOnlyOneSite || siteIds[0] === null)) {
                            siteIds = [];
                        }
                        return referralsService.hasUnacknowledgedReferrals(org.orgCode, siteIds);
                    })
                )
                .subscribe(hasUnacknowledgedReferrals => {
                    this.onUpdateReferralsAwaitingAcknowledgementCount(hasUnacknowledgedReferrals);
                })
        );
    }

    onSelectedOrgChanged(selectedOrg: Org) {
        const user = this.authenticationService.getCurrentUser();

        const ordersTabVisible = user.hasRole(RoleEnum.GeneralAccess, selectedOrg.orgCode);

        const referralsTabVisible =
            this.featuresService.isFeatureEnabled('referralsTab', selectedOrg.countryCode) &&
            user.hasRole(RoleEnum.GeneralAccess, selectedOrg.orgCode);

        const clinicalFeedbackTabVisible = user.hasRole(RoleEnum.ClinicalFeedback, selectedOrg.orgCode);

        const imagingRequestsTabVisible =
            (user.hasRole(RoleEnum.ImagingRequestViewer, selectedOrg.orgCode) ||
                user.hasRole(RoleEnum.ImagingRequestEditor, selectedOrg.orgCode)) &&
            SettingUtilities.isSettingEnabled(selectedOrg.settings, SettingType.ImagingOverviewEnabled);

        const demandManagementTabVisible =
            user.hasRole(RoleEnum.DemandManagement, selectedOrg.orgCode) &&
            SettingUtilities.isSettingEnabled(selectedOrg.settings, SettingType.DemandManagement);

        this.setTabVisibility(Tab.Orders, ordersTabVisible);
        this.setTabVisibility(Tab.Referrals, referralsTabVisible);
        this.setTabVisibility(Tab.ClinicalFeedback, clinicalFeedbackTabVisible);
        this.setTabVisibility(Tab.ImagingRequests, imagingRequestsTabVisible);
        this.setTabVisibility(Tab.DemandManagement, demandManagementTabVisible);

        if (!DashboardComponent.hasSetInitialTab.value && this.getActiveTab() === Tab.Unknown && imagingRequestsTabVisible) {
            this.setActiveTab(Tab.ImagingRequests);
            DashboardComponent.hasSetInitialTab.next(true);
        } else if (this.getActiveTab() === Tab.Unknown) {
            const firstVisibleTab = this.getFirstVisibleTab();
            if (firstVisibleTab !== Tab.Unknown) {
                this.setActiveTab(firstVisibleTab);
            } else {
                // No tab visible, user does not have permissions to see any tab.
                const nextAvailableRoute = this.permissionRoutingService.getNextAvailableRoute(user, this.router.url, selectedOrg.orgCode);
                this.router.navigate([nextAvailableRoute]);
            }
        }

        this.orgSiteSelectionService.setTab(this.getActiveTab());

        this.referralsTabVisible$.next(referralsTabVisible);
    }

    onOrdersCountUpdated(count: number): void {
        if (this.getActiveTab() === Tab.Orders) {
            this.dashboardTabs.tabItems[Tab.Orders].labelCount = `(${count})`;
        }
    }

    onReferralsCountUpdated(count: number): void {
        if (this.getActiveTab() === Tab.Referrals) {
            this.dashboardTabs.tabItems[Tab.Referrals].labelCount = `(${count})`;
        }
    }

    onUpdateReferralsAwaitingAcknowledgementCount(hasUnacknowledgedReferrals: boolean) {
        this.hasReferralsAwaitingAcknowledgement = hasUnacknowledgedReferrals;
        this.referralsService.hasReferralsAwaitingAcknowledgement$.next(hasUnacknowledgedReferrals);
    }

    onTabNavigation(event: RouterEvent): void {
        if (event instanceof NavigationStart) {
            this.dashboardTabs.tabItems[Tab.Orders].labelCount = '';
            this.dashboardTabs.tabItems[Tab.Referrals].labelCount = '';
        }
        if (event instanceof NavigationEnd) {
            this.orgSiteSelectionService.setTab(this.getActiveTab());
        }
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribe();
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    private setTabVisibility(tab: Tab, isVisible: boolean) {
        this.dashboardTabs.tabItems[tab].isVisible = isVisible;
    }

    private setActiveTab(tab: Tab) {
        this.router.navigate([`${DashboardComponent.DASHBOARD_PATH}/${this.dashboardTabs.tabItems[tab].routerLink}`]);
    }

    private getActiveTab(): Tab {
        for (const [tab, tabOptions] of Object.entries(this.dashboardTabs.tabItems as Record<Tab, TabOptions>)) {
            if (this.router.url.endsWith(tabOptions.routerLink) && tabOptions.isVisible) {
                return tab as Tab;
            }
        }
        return Tab.Unknown;
    }

    private getFirstVisibleTab(): Tab {
        for (const [tab, tabOptions] of Object.entries(this.dashboardTabs.tabItems as Record<Tab, TabOptions>)) {
            if (tabOptions.isVisible) {
                return tab as Tab;
            }
        }
        return Tab.Unknown;
    }
}

export interface DashboardTabs {
    tabItems: Record<Tab, TabOptions>;
    [Symbol.iterator](): IterableIterator<TabOptions>;
}

export interface TabOptions {
    htmlId: string;
    title: string;
    labelCount?: string;
    routerLink: string;
    isVisible: boolean;
}
